import { modelOptions, PropType, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { FirstPartyDeviceType, SensorType } from "../../../shared.js";
import { milliseconds } from "../../../timeConstants.js";
import { getZodSchemaFromClass, SensorTypeString, validate } from "../../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../schemaBase.js";
import {
    CustomSensorTimingRuleFieldsWithDefaults,
    CustomSensorTimingRuleSchemaTypes,
} from "../../types/customSensorTimingRule.js";
import { DtoValidatorTypes } from "../../validatableClass.js";
import { SensorParametersForCustomTimingRules } from "../types.js";

const SCHEMA_KEY = "CustomSensorTimingRule" as const;

@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<CustomSensorTimingRule>>(...addSchemaKeyHook(SCHEMA_KEY))
export class CustomSensorTimingRule extends SchemaBase<
    CustomSensorTimingRule,
    CustomSensorTimingRuleFieldsWithDefaults
> {
    static schemaKey = SCHEMA_KEY;
    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    /**
     * An empty array or this being undefined both mean that the sensor count won't be considered
     * when checking if a device satisfies this rule
     */
    @validate({ zodValidator: z.array(z.number()).optional() })
    @prop({ type: () => Number, required: false }, PropType.ARRAY)
    public acceptableSensorCounts?: number[];

    /**
     * An empty array or this being undefined both mean that the device type won't be considered
     * when checking if a device satisfies this rule
     */
    @validate({ zodValidator: z.array(z.string()).optional() })
    @prop({ type: () => String, required: false }, PropType.ARRAY)
    public acceptableDeviceTypes?: FirstPartyDeviceType[];

    /**
     * An empty array or this being undefined both mean that the sensor type won't be considered
     * when checking if a device satisfies this rule
     */
    @validate({ zodValidator: z.array(SensorTypeString).optional() })
    @prop({ type: () => String, required: false }, PropType.ARRAY)
    public acceptableSensorTypes?: SensorType[];

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public deviceNotesKeyword?: string;

    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: false })
    public readingPeriod?: milliseconds;

    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: false })
    public uplinkPeriod?: milliseconds;

    static getters = {} as const satisfies Record<string, (self: CustomSensorTimingRule) => any>;

    static utilities = {
        doesSensorSatisfyRule: (
            self: CustomSensorTimingRule,
            parameters: SensorParametersForCustomTimingRules,
        ): boolean => {
            if (
                self.acceptableSensorCounts &&
                self.acceptableSensorCounts.length > 0 &&
                !self.acceptableSensorCounts.includes(parameters.sensorCount)
            ) {
                return false;
            }
            if (
                self.acceptableSensorTypes &&
                self.acceptableSensorTypes.length > 0 &&
                !self.acceptableSensorTypes.includes(parameters.sensorType)
            ) {
                return false;
            }
            if (
                self.acceptableDeviceTypes &&
                self.acceptableDeviceTypes.length > 0 &&
                !self.acceptableDeviceTypes.includes(parameters.deviceType)
            ) {
                return false;
            }
            if (self.deviceNotesKeyword && !parameters.notes.includes(`sense:${self.deviceNotesKeyword}`)) {
                return false;
            }
            return true;
        },
    } as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<CustomSensorTimingRuleSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<CustomSensorTimingRule, CustomSensorTimingRuleFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

const CustomSensorTimingRuleSchema = getZodSchemaFromClass(CustomSensorTimingRule);

CustomSensorTimingRule.dtoValidators =
    CustomSensorTimingRuleSchema as unknown as DtoValidatorTypes<CustomSensorTimingRuleSchemaTypes>;
